import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Initialize, KairaModal } from "kaira-cp";
import "./PatientSidebar.scss";

import { PatientDetails } from "../../../types/patient";
import { getAgeByDob } from "../../../utils/date-and-time";
import { getInitials } from "../../../utils/common";
import { getPatientId } from "../../../utils/patientSession";
import PatientProfileModal from "../../../molecules/PatientDetailsV2/PatientProfileModal/PatientProfileModal";
import CompletionRing from "../CoachAssessment/CompletionRing";
import {
  CareTriadKey,
  useAssessmentProgress,
} from "../CoachAssessment/AssessmentProgressContext";
import { RootState } from "../../../store/store";
import environment from "../../../constants/environments";

import { ReactComponent as OverviewIcon } from "../../../assets/icons/patient-layout-v2/sidebar/OverviewIcon.svg";
import { ReactComponent as ChatKairaIcon } from "../../../assets/icons/patient-layout-v2/sidebar/ChatKairaIcon.svg";
import { ReactComponent as SharedProfileIcon } from "../../../assets/icons/patient-layout-v2/sidebar/SharedProfileIcon.svg";
import { ReactComponent as RecordsIcon } from "../../../assets/icons/patient-layout-v2/sidebar/RecordsIcon.svg";
import { ReactComponent as LabVitalsIcon } from "../../../assets/icons/patient-layout-v2/sidebar/LabVitalsIcon.svg";
import { ReactComponent as BcaIcon } from "../../../assets/icons/patient-layout-v2/sidebar/BcaIcon.svg";
import { ReactComponent as CgmIcon } from "../../../assets/icons/patient-layout-v2/sidebar/CgmIcon.svg";
import { ReactComponent as ConsultsIcon } from "../../../assets/icons/patient-layout-v2/sidebar/ConsultsIcon.svg";
import { ReactComponent as ProgressIcon } from "../../../assets/icons/patient-layout-v2/sidebar/ProgressIcon.svg";
import { ReactComponent as OtherTrackersIcon } from "../../../assets/icons/patient-layout-v2/sidebar/OtherTrackersIcon.svg";
import { ReactComponent as CaretIcon } from "../../../assets/icons/patient-layout-v2/sidebar/CaretIcon.svg";

const getKairaCoachId = () => {
  const fromStorage = localStorage.getItem("health_coach_id");
  if (fromStorage) return fromStorage;

  try {
    const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
    return userInfo?.health_coach_id || null;
  } catch {
    return null;
  }
};

const initializeKairaFromSession = ({
  coachId: coachIdOverride,
  patientId: patientIdOverride,
}: {
  coachId?: string | null;
  patientId?: string | null;
} = {}) => {
  const accessToken = localStorage.getItem("token");
  const coachId = coachIdOverride || getKairaCoachId();
  const patientId =
    patientIdOverride || getPatientId() || null;

  if (!accessToken || !coachId) return;

  Initialize({
    userID: patientId,
    accessToken,
    kairaApiBaseUrl: environment.REACT_APP_KAIRA_API_BASE_URL,
    coachId,
    extraHeaders: {
      source: "coach-portal",
    },
  });
};

const getPlanDuration = (days?: number | null) => {
  if (!days) return "--";
  if (days >= 30) {
    const months = Math.floor(days / 30);
    return `${months} Month${months > 1 ? "s" : ""}`;
  }
  return `${days} Day${days > 1 ? "s" : ""}`;
};

const RECORD_NAV_ITEMS = [
  {
    key: "shared",
    label: "Shared Profile",
    icon: <SharedProfileIcon className="ico" />,
  },
  { key: "records", label: "Records", icon: <RecordsIcon className="ico" /> },
  {
    key: "labvitals",
    label: "Lab Vitals",
    icon: <LabVitalsIcon className="ico" />,
  },
  {
    key: "bca",
    label: "BCA Report",
    icon: <BcaIcon className="ico" />,
    comingSoon: true,
  },
  {
    key: "cgm",
    label: "CGM Report",
    icon: <CgmIcon className="ico" />,
    comingSoon: true,
  },
  {
    key: "consults",
    label: "Consultations",
    icon: <ConsultsIcon className="ico" />,
  },
];

const PROGRESS_ITEMS = [
  { key: "main-goals", label: "Main Goals" },
  { key: "custom-goals", label: "Custom Goals" },
  { key: "assessment-recap", label: "Assessment Recap" },
];

// Trackers pulled out of Care Triad coaches into their own Progress-style
// block so coaches can find them without digging under Exercise / Success.
const OTHER_TRACKERS = [
  { key: "incentive-spirometer", label: "Incentive Spirometer" },
  { key: "steps", label: "Steps" },
  { key: "water-intake", label: "Water Intake" },
  { key: "sleep", label: "Sleep" },
];

interface CareTriadSubItem {
  key: string;
  label: string;
  kind?: "baseline" | "followup" | "tool";
}

const CARE_TRIAD: {
  key: CareTriadKey;
  label: string;
  color: string;
  comingSoon?: boolean;
  subItems: CareTriadSubItem[];
}[] = [
  {
    key: "diet",
    label: "Diet Coach",
    color: "var(--diet)",
    subItems: [
      { key: "diet-baseline", label: "Baseline Assessment", kind: "baseline" },
      { key: "diet", label: "Follow-up Assessments", kind: "followup" },
      { key: "diet-plans", label: "List of Diet Plans", kind: "tool" },
      { key: "diet-adherence", label: "Adherence & User View", kind: "tool" },
      { key: "diet-reports", label: "Diet Reports", kind: "tool" },
    ],
  },
  {
    key: "exercise",
    label: "Exercise Coach",
    color: "var(--exercise)",
    subItems: [
      {
        key: "exercise-baseline",
        label: "Baseline Assessment",
        kind: "baseline",
      },
      { key: "exercise", label: "Follow-up Assessments", kind: "followup" },
      { key: "exercise-plan", label: "Exercise Plan", kind: "tool" },
    ],
  },
  {
    key: "success",
    label: "Success Coach",
    color: "var(--success)",
    subItems: [
      { key: "success-baseline", label: "Baseline Assessment", kind: "baseline" },
      { key: "success", label: "Follow-up Assessments", kind: "followup" },
    ],
  },
];

interface PatientSidebarProps {
  activeView: string;
  onNavigate: (view: string) => void;
  onConditionSaved?: () => void;
  patient?: PatientDetails | null;
}

const PatientSidebar: React.FC<PatientSidebarProps> = ({
  activeView,
  onNavigate,
  onConditionSaved,
  patient,
}) => {
  const params = useParams();
  const { healthCoachDetails } = useSelector(
    (state: RootState) => state.healthCoach,
  );
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const [isKairaChatOpen, setIsKairaChatOpen] = useState(false);
  const profileModalRef = useRef<any>(null);
  const { pctByView, labelByView } = useAssessmentProgress();
  const visibleTriad = CARE_TRIAD;

  useEffect(() => {
    setAvatarLoadFailed(false);
  }, [patient?.profile_pic]);

  const isProgressActive = PROGRESS_ITEMS.some(
    (item) => item.key === activeView,
  );
  const isOtherTrackersActive = OTHER_TRACKERS.some(
    (item) => item.key === activeView,
  );

  const patientName = patient?.name || "Patient";
  const patientGender =
    patient?.gender === "M" ? "Male" : patient?.gender === "F" ? "Female" : "";
  const patientAge =
    getAgeByDob(patient?.dob ?? null) ??
    (patient?.patient_age ? Number(patient.patient_age) : null);
  const patientCondition = patient?.medical_condition_name || patient?.disease;

  const ageGenderTag = [patientAge ? `${patientAge}` : null, patientGender]
    .filter(Boolean)
    .join(" · ");

  const patientId =
    patient?.patient_id ||
    params.patientId ||
    getPatientId() ||
    "";
  const coachId =
    healthCoachDetails?.data?.health_coach_id ||
    params.healthCoachId ||
    getKairaCoachId();
  const patientPlanName = patient?.plan_name || "--";
  const patientPlanDuration = getPlanDuration(patient?.plan_duration);
  const patientPlanStatus = (() => {
    const expiryRaw = patient?.plan_expiry_datetime;
    if (!expiryRaw) return null;
    const expiry = new Date(expiryRaw);
    if (Number.isNaN(expiry.getTime())) return null;
    return expiry > new Date() ? "active" : "inactive";
  })();

  const openKairaChat = () => {
    initializeKairaFromSession({ coachId, patientId });
    setIsKairaChatOpen(true);
  };

  return (
    <aside className="patient-sidebar">
      <button
        className="side-patient"
        title="View patient, plan & account details"
        onClick={() => profileModalRef.current?.showPatientDetails()}
      >
        <div className="avatar">{getInitials(patientName)}</div>
        <div className="sp-meta">
          <div className="patient-name">{patientName}</div>
          <div className="patient-tags">
            {ageGenderTag && <span className="ptag">{ageGenderTag}</span>}
            {!ageGenderTag && !patientCondition && (
              <span className="ptag">No details available</span>
            )}
          </div>
          {patientCondition && (
            <div className="patient-indication">
              <span className="indication-label">Indication:</span>{" "}
              <span className="indication-value">{patientCondition}</span>
            </div>
          )}
        </div>
        <CaretIcon className="caret" />
      </button>

      <div className="nav-group">
        <button
          type="button"
          className={`nav-item ${activeView === "overview" ? "active" : ""}`}
          onClick={() => onNavigate("overview")}
        >
          <OverviewIcon className="ico" />
          Overview
        </button>
        <button
          className={`nav-item ${isKairaChatOpen ? "active" : ""}`}
          onClick={openKairaChat}
        >
          <ChatKairaIcon className="ico" />
          Chat with Kaira
        </button>
      </div>

      <div className="nav-group">
        <div className="nav-label">Patient Record</div>
        {RECORD_NAV_ITEMS.map((item) => (
          <button
            key={item.key}
            className={`nav-item ${
              !item.comingSoon && activeView === item.key ? "active" : ""
            }`}
            disabled={item.comingSoon}
            onClick={
              item.comingSoon ? undefined : () => onNavigate(item.key)
            }
          >
            {item.icon}
            {item.label}
            {item.comingSoon && <span className="soon-tag">Soon</span>}
          </button>
        ))}
        <button
          className={`nav-item ${isProgressActive ? "active" : ""}`}
          onClick={() => onNavigate(PROGRESS_ITEMS[0].key)}
        >
          <ProgressIcon className="ico" />
          Progress &amp; Goals
        </button>
        {PROGRESS_ITEMS.map((item) => (
          <button
            key={item.key}
            className={`nav-item nav-sub ${
              activeView === item.key ? "active" : ""
            }`}
            onClick={() => onNavigate(item.key)}
          >
            <span className="sub-line" />
            <span className="nav-sub-hit">{item.label}</span>
          </button>
        ))}
        <button
          className={`nav-item ${isOtherTrackersActive ? "active" : ""}`}
          onClick={() => onNavigate(OTHER_TRACKERS[0].key)}
        >
          <OtherTrackersIcon className="ico" />
          Other Trackers
        </button>
        {OTHER_TRACKERS.map((item) => (
          <button
            key={item.key}
            className={`nav-item nav-sub ${
              activeView === item.key ? "active" : ""
            }`}
            onClick={() => onNavigate(item.key)}
          >
            <span className="sub-line" />
            {item.label}
          </button>
        ))}
      </div>

      {visibleTriad.length > 0 && (
      <div className="nav-group">
        <div className="nav-label">Care Triad</div>
        {visibleTriad.map((coach) => {
          const subItems = coach.subItems;
          const isCoachActive =
            !coach.comingSoon &&
            subItems.some((sub) => sub.key === activeView);
          const firstSubKey = subItems[0]?.key;
          const latestIsFollowUp = (labelByView[coach.key] || "").startsWith(
            "Follow-up",
          );

          return (
            <React.Fragment key={coach.key}>
              <button
                type="button"
                className={`nav-item nav-coach ${isCoachActive ? "active" : ""}`}
                style={{ ["--c" as any]: coach.color }}
                disabled={coach.comingSoon || !subItems.length}
                onClick={
                  coach.comingSoon || !firstSubKey
                    ? undefined
                    : () => onNavigate(firstSubKey)
                }
              >
                <span className="dot" style={{ background: coach.color }} />
                {coach.label}
                {coach.comingSoon ? (
                  <span className="soon-tag">Soon</span>
                ) : (
                  <>
                    <span className="spacer" />
                    <CompletionRing
                      pct={pctByView[coach.key]}
                      color={
                        coach.key === "diet"
                          ? "#299d6b"
                          : coach.key === "exercise"
                            ? "#2E8E92"
                            : "#876BA6"
                      }
                    />
                  </>
                )}
              </button>
              {subItems.map((sub) => {
                const showLatest =
                  (sub.kind === "followup" && latestIsFollowUp) ||
                  (sub.kind === "baseline" && !latestIsFollowUp);
                return (
                  <button
                    key={`${coach.key}-${sub.key}`}
                    type="button"
                    className={`nav-item nav-sub ${
                      activeView === sub.key ? "active" : ""
                    }`}
                    onClick={() => onNavigate(sub.key)}
                  >
                    <span className="sub-line" />
                    <span className="nav-sub-hit">
                      {sub.label}
                      {showLatest ? (
                        <span
                          className="nav-live"
                          style={{ ["--live" as any]: coach.color }}
                        />
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
      )}

      <PatientProfileModal
        ref={profileModalRef}
        onConditionSaved={onConditionSaved}
      />
      <PatientProfileModal ref={profileModalRef} />
      {isKairaChatOpen && patientId && (
        <KairaModal
          isOpen={isKairaChatOpen}
          onClose={() => setIsKairaChatOpen(false)}
          patient={{
            id: patientId,
            name: patientName || "Patient",
            plan: patientPlanName !== "--" ? patientPlanName : undefined,
            duration:
              patientPlanDuration !== "--" ? patientPlanDuration : undefined,
            status: patientPlanStatus || undefined,
          }}
        />
      )}
    </aside>
  );
};

export default PatientSidebar;
